import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  type Unsubscribe,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { db } from '@/lib/firebase';

const NATIVE_TRIP_CARD_STATES_POLL_MS = 30000;

export type TripCardVisibility = 'expanded' | 'minimized' | 'hidden';

export type TripCardState = {
  tripId: string;
  visibility: TripCardVisibility;
  dismissedByUser?: boolean;
  dismissedAt?: Date | null;
  minimizedAt?: Date | null;
  autoHiddenAt?: Date | null;
  lastUpdatedAt?: Date | null;
  tripEndDate?: Date | null;
};

type VisibilityReason = 'user' | 'auto' | 'system';

const tripCardCollection = (userId: string) => collection(db, 'users', userId, 'tripCardStates');

const convertTimestamp = (value?: Timestamp | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const parseStateDoc = (docSnapshot: QueryDocumentSnapshot<DocumentData>): TripCardState => {
  const data = docSnapshot.data();

  return {
    tripId: docSnapshot.id,
    visibility: (data.visibility as TripCardVisibility) ?? 'expanded',
    dismissedByUser: Boolean(data.dismissedByUser),
    dismissedAt: convertTimestamp(data.dismissedAt),
    minimizedAt: convertTimestamp(data.minimizedAt),
    autoHiddenAt: convertTimestamp(data.autoHiddenAt),
    lastUpdatedAt: convertTimestamp(data.lastUpdatedAt),
    tripEndDate: convertTimestamp(data.tripEndDate),
  };
};

export function subscribeToTripCardStates(userId: string, callback: (states: TripCardState[]) => void): Unsubscribe {
  if (Platform.OS !== 'web') {
    let isCleanedUp = false;
    const poll = async () => {
      try {
        const snapshot = await getDocs(tripCardCollection(userId));
        const states = snapshot.docs.map(parseStateDoc);
        if (!isCleanedUp) {
          callback(states);
        }
      } catch (error) {
        console.error('[TripCardStates] Native polling fetch failed:', error);
        if (!isCleanedUp) {
          callback([]);
        }
      }
    };
    void poll();
    const interval = setInterval(() => {
      void poll();
    }, NATIVE_TRIP_CARD_STATES_POLL_MS);
    return () => {
      isCleanedUp = true;
      clearInterval(interval);
    };
  }

  return onSnapshot(tripCardCollection(userId), (snapshot) => {
    const states = snapshot.docs.map(parseStateDoc);
    callback(states);
  });
}

type SetVisibilityOptions = {
  reason?: VisibilityReason;
  tripEndDate?: Date | null;
};

export async function setTripCardVisibility(
  userId: string,
  tripId: string,
  visibility: TripCardVisibility,
  options?: SetVisibilityOptions,
): Promise<void> {
  const ref = doc(tripCardCollection(userId), tripId);
  const payload: Record<string, unknown> = {
    visibility,
    lastUpdatedAt: serverTimestamp(),
  };

  if (visibility === 'minimized') {
    payload.minimizedAt = serverTimestamp();
    payload.dismissedByUser = false;
  }

  if (visibility === 'hidden') {
    if (options?.reason === 'auto') {
      payload.autoHiddenAt = serverTimestamp();
      payload.dismissedByUser = false;
    } else {
      payload.dismissedByUser = true;
      payload.dismissedAt = serverTimestamp();
    }
  }

  if (visibility === 'expanded') {
    payload.dismissedByUser = false;
  }

  if (options?.tripEndDate) {
    payload.tripEndDate = Timestamp.fromDate(options.tripEndDate);
  }

  await setDoc(ref, payload, { merge: true });
}

export async function clearTripCardState(userId: string, tripId: string): Promise<void> {
  await deleteDoc(doc(tripCardCollection(userId), tripId));
}


